import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from '@shared';
import { distinctUntilChanged, map, merge, Observable, of, shareReplay, Subject } from 'rxjs';

/** Shared admin header state: filter lists by service zone. Default is all zones (global). */
@Injectable({ providedIn: 'root' })
export class AdminZoneFilterService {
  private static readonly UUID =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  private readonly authService = inject(AuthService);

  /** `'all'` = no API zone filter (global); otherwise a service zone UUID. */
  readonly selectedZoneId = signal<'all' | string>('all');

  private readonly zoneChangeSubject = new Subject<void>();

  /** Emits when the header zone selection changes. */
  readonly zoneChange$: Observable<void> = this.zoneChangeSubject.asObservable();

  /**
   * Emits current zone API params when header zone or profile changes.
   * Use with `switchMap` so the active page reloads in the selected zone.
   */
  private readonly zoneScopeParams$ = merge(
    of(undefined),
    this.zoneChange$,
    toObservable(this.selectedZoneId),
  ).pipe(
    map(() => {
      const profile = this.authService.profile();
      return this.apiZoneParams(profile?.role, profile);
    }),
    distinctUntilChanged(
      (a, b) => (a.serviceZoneId ?? '') === (b.serviceZoneId ?? ''),
    ),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  watchZoneScope(): Observable<{ serviceZoneId?: string }> {
    return this.zoneScopeParams$;
  }

  /**
   * Query params for zone-scoped APIs (`orders`, `vendors`, `users`, …).
   * Zone admins: always their JWT zone. Admin/superAdmin: header selection only.
   */
  apiZoneParams(
    role: string | undefined,
    profile?: { serviceZoneId?: string } | null,
  ): { serviceZoneId?: string } {
    return this.ordersApiZoneParams(role, profile);
  }

  /** @deprecated Prefer {@link apiZoneParams}. */
  ordersApiZoneParams(
    role: string | undefined,
    profile?: { serviceZoneId?: string } | null,
  ): { serviceZoneId?: string } {
    if (role === 'zoneAdmin' && profile?.serviceZoneId && AdminZoneFilterService.UUID.test(profile.serviceZoneId)) {
      return { serviceZoneId: profile.serviceZoneId };
    }
    if (role !== 'admin' && role !== 'superAdmin') {
      return {};
    }
    const id = this.selectedZoneId();
    if (id && id !== 'all' && AdminZoneFilterService.UUID.test(id)) {
      return { serviceZoneId: id };
    }
    return {};
  }

  /** Active zone UUID from header/profile, or undefined when global (all zones). */
  activeZoneUuid(
    role: string | undefined,
    profile?: { serviceZoneId?: string } | null,
  ): string | undefined {
    if (role === 'zoneAdmin' && profile?.serviceZoneId && AdminZoneFilterService.UUID.test(profile.serviceZoneId)) {
      return profile.serviceZoneId;
    }
    return this.selectedServiceZoneUuid();
  }

  /** Whether lists should be global (no zone filter). */
  isGlobalScope(role: string | undefined, profile?: { serviceZoneId?: string } | null): boolean {
    return !this.activeZoneUuid(role, profile);
  }

  selectedServiceZoneUuid(): string | undefined {
    const id = this.selectedZoneId();
    if (id && id !== 'all' && AdminZoneFilterService.UUID.test(id)) {
      return id;
    }
    return undefined;
  }

  /** Client-side filter when API has no zone param (e.g. legacy lists). */
  filterByZone<T extends { serviceZoneId?: string | null }>(
    items: T[],
    role: string | undefined,
    profile?: { serviceZoneId?: string } | null,
  ): T[] {
    const zoneId = this.activeZoneUuid(role, profile);
    if (!zoneId) {
      return items;
    }
    return items.filter((item) => item.serviceZoneId != null && item.serviceZoneId === zoneId);
  }

  selectZone(zoneId: 'all' | string): void {
    const raw = zoneId === 'all' ? 'all' : String(zoneId).trim();
    if (raw !== 'all' && !AdminZoneFilterService.UUID.test(raw)) {
      return;
    }
    const next = raw === 'all' ? 'all' : raw;
    if (this.selectedZoneId() === next) {
      return;
    }
    this.selectedZoneId.set(next);
    this.zoneChangeSubject.next();
  }

  /**
   * Pin header zone selection to a zone admin's assigned zone so zone-scoped
   * settings and lists load that zone by default after login.
   */
  syncZoneAdminSelection(profile?: { role?: string; serviceZoneId?: string } | null): void {
    if (profile?.role !== 'zoneAdmin' || !profile.serviceZoneId) {
      return;
    }
    if (!AdminZoneFilterService.UUID.test(profile.serviceZoneId)) {
      return;
    }
    this.selectZone(profile.serviceZoneId);
  }
}
